/**
 * Finite, owner-requested US city family. These are candidate identities, NOT
 * admitted routes, source-captured legacy pages, destination links or sitemap members.
 * Centers: GeoNames cities15000 export, read 2026-10-12. GeoNames populated-place
 * points define this count boundary; Maryland deliberately means Baltimore.
 */
export const US_CITY_RADIUS_KM = 64.37376;
export const US_CITY_MINIMUM_CARS = 8;
const rows = [
  ["Denver", "CO", "denver", 39.73915, -104.9847, 5419384],
  ["New York", "NY", "new-york", 40.71427, -74.00597, 5128581],
  ["Houston", "TX", "houston", 29.76328, -95.36327, 4699066],
  ["Cincinnati", "OH", "cincinnati", 39.12711, -84.51439, 4508722],
  ["Tampa", "FL", "tampa", 27.94752, -82.45843, 4174757],
  ["Minneapolis", "MN", "minneapolis", 44.97997, -93.26384, 5037649],
  ["Pittsburgh", "PA", "pittsburgh", 40.44062, -79.99589, 5206379],
  ["Santa Barbara", "CA", "santa-barbara", 34.42083, -119.69819, 5392952],
  ["St Louis", "MO", "st-louis", 38.62727, -90.19789, 4407066],
  ["Charlotte", "NC", "charlotte", 35.22709, -80.84313, 4460243],
  ["Sacramento", "CA", "sacramento", 38.58157, -121.4944, 5389489],
  ["Portland", "OR", "portland", 45.52345, -122.67621, 5746545],
  ["Indianapolis", "IN", "indianapolis", 39.76838, -86.15804, 4259418],
  ["San Antonio", "TX", "san-antonio", 29.42412, -98.49363, 4726206],
  ["Kansas City", "MO", "kansas-city", 39.09973, -94.57857, 4393217],
  ["Oklahoma City", "OK", "oklahoma-city", 35.46756, -97.51643, 4544349],
  ["Columbus", "OH", "columbus", 39.96118, -82.99879, 4509177],
  ["Baltimore / Maryland", "MD", "maryland", 39.29038, -76.61219, 4347778],
  ["Fort Worth", "TX", "fort-worth", 32.72541, -97.32085, 4691930],
  ["Salt Lake City", "UT", "salt-lake-city", 40.76078, -111.89105, 5780993],
];
export const US_CITY_CANDIDATES = Object.freeze(rows.map(
  ([city, state, slug, latitude, longitude, geonameId], index) => Object.freeze({
    priority: index + 1, city, state, path: `/classic-car-rental-${slug}`,
    latitude, longitude, geonameId, centerSource: `https://www.geonames.org/${geonameId}`,
  }),
));
const paths = new Set(US_CITY_CANDIDATES.map(city => city.path));
export function isUsCityExpansionPath(path) { return paths.has(path); }

export function cityDistanceKm(car, center) {
  const latitude = car.latitude, longitude = car.longitude;
  if (latitude == null || longitude == null || latitude === "" || longitude === "" ||
      !Number.isFinite(Number(latitude)) || !Number.isFinite(Number(longitude)) ||
      Math.abs(Number(latitude)) > 90 || Math.abs(Number(longitude)) > 180) return null;
  const r = Math.PI / 180;
  const a = Math.sin((Number(latitude) - center.latitude) * r / 2) ** 2 +
    Math.cos(Number(latitude) * r) * Math.cos(center.latitude * r) *
    Math.sin((Number(longitude) - center.longitude) * r / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(Math.max(0, 1 - a)));
}

/** Diagnostic only: preserve saved ordering; never publish or rank live requests. */
export function savedCityStock(cars, center) {
  const seen = new Set(), local = [], nearest = [];
  let unknownCoordinates = 0;
  for (const car of cars) {
    if (seen.has(car.id) || car.countryCode !== "US") continue;
    seen.add(car.id);
    const distanceKm = cityDistanceKm(car, center);
    if (distanceKm == null) { unknownCoordinates++; continue; }
    const entry = { car, distanceKm };
    nearest.push(entry);
    if (distanceKm <= US_CITY_RADIUS_KM) local.push(entry);
  }
  return {
    local, unknownCoordinates,
    // Explicitly separate diagnostic nearest fallback from saved city-family order.
    nearest: nearest.sort((a, b) => a.distanceKm - b.distanceKm || a.car.id - b.car.id).slice(0, 8),
  };
}

/** Fail closed without lowering any existing route's stock minimum or OFF safeguards. */
export function cityAdmissionProblems({ path, inventory, cars, countRead, collisions = [], now = Date.now() }) {
  if (!isUsCityExpansionPath(path)) throw new Error("Not a requested US expansion path");
  const center = US_CITY_CANDIDATES.find(city => city.path === path), problems = [];
  if (collisions.length) problems.push("existing-path-ownership");
  if (!countRead || countRead.state !== "captured") problems.push("production-count-unverified");
  if (!inventory || inventory.state !== "ready" || !inventory.generationId ||
      !Number.isFinite(Date.parse(inventory.nextRefreshAt)) ||
      Date.parse(inventory.nextRefreshAt) <= now) problems.push("fresh-saved-inventory-unverified");
  const stock = savedCityStock(cars || [], center);
  if (stock.local.length < US_CITY_MINIMUM_CARS) problems.push("fewer-than-eight-saved-local-cars");
  if (stock.unknownCoordinates) problems.push("saved-coordinate-uncertainty");
  if (stock.local.some(({ car }) => !car.images?.some(image =>
    image.width > 0 && image.height > 0 && /^https:\/\//.test(image.cdnMedium || image.url || "")))
  ) problems.push("verified-local-image-dimensions-unavailable");
  // Aggregate totals cannot certify which saved IDs are currently active/renderable.
  if (countRead?.savedActiveLocalCount !== stock.local.length) problems.push("saved-active-membership-unverified");
  return problems;
}
